import styled from 'styled-components';

import { simpleDate } from 'lib/date';
import { capitalize } from 'lib/util';
import { LocationEpisodeLink } from 'models/flexible/LocationEpisodeLink';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall } from 'styles/typography';

import StatusBadge from '../StatusBadge';

type Props = {
  link: LocationEpisodeLink;
  label: string;
  showRemove?: boolean;
  showRelink?: boolean;
  showPending?: boolean;
  isArchived?: boolean;
  onRemove?: () => void;
  onRelink?: () => void;
};

export default function RecordCard({
  link,
  label,
  showRemove = false,
  showRelink = false,
  showPending = false,
  isArchived = false,
  onRemove,
  onRelink,
}: Props) {
  const { locationEpisode } = link;

  return (
    <Card $isArchived={isArchived}>
      <CardHeader>
        <HeaderTitle>Client Record {label}</HeaderTitle>
        {showRemove && <RemoveLink onClick={onRemove}>Remove</RemoveLink>}
        {showRelink && <RelinkButton onClick={onRelink}>Relink</RelinkButton>}
        {showPending && <StatusBadge status='pending' />}
      </CardHeader>

      <FieldRow>
        <FieldContent>
          <FieldLabel>Patient Name</FieldLabel>
          <FieldValue>{link.patientName || '-'}</FieldValue>
        </FieldContent>
        <ViewLink
          href={`/patients/${locationEpisode?.episodeId}?locationEpisodeId=${locationEpisode?.id}`}
          target='_blank'
          rel='noopener noreferrer'>
          View Patient Story
        </ViewLink>
      </FieldRow>

      <FieldRow>
        <FieldContent>
          <FieldLabel>DOB</FieldLabel>
          <FieldValue>{link.dateOfBirth ? simpleDate(link.dateOfBirth) : '-'}</FieldValue>
        </FieldContent>
      </FieldRow>

      <FieldRow>
        <FieldContent>
          <FieldLabel>Sex</FieldLabel>
          <FieldValue>{capitalize(link.sex) || '-'}</FieldValue>
        </FieldContent>
      </FieldRow>

      <FieldRow>
        <FieldContent>
          <FieldLabel>Client</FieldLabel>
          <FieldValue>{locationEpisode?.owner?.name ?? '-'}</FieldValue>
        </FieldContent>
      </FieldRow>

      <FieldRow $noBorder>
        <FieldContent>
          <FieldLabel>Location</FieldLabel>
          <FieldValue>{link.groupName || '-'}</FieldValue>
        </FieldContent>
      </FieldRow>
    </Card>
  );
}

const Card = styled.div<{ $isArchived?: boolean }>`
  display: flex;
  flex-direction: column;
  opacity: ${(props) => (props.$isArchived ? 0.5 : 1)};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const HeaderTitle = styled(Body)`
  font-weight: 500;
  font-size: 14px;
  margin: 0;
`;

const RelinkButton = styled.button`
  background: none;
  border: none;
  color: ${colors.blue100};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const RemoveLink = styled.button`
  background: none;
  border: none;
  color: ${colors.red100};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const FieldRow = styled.div<{ $noBorder?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: ${(props) => (props.$noBorder ? 'none' : `1px solid ${colors.black10}`)};
`;

const FieldContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 12px;
`;

const FieldLabel = styled(BodySmall)`
  font-weight: 500;
  font-size: 12px;
  margin: 0;
`;

const FieldValue = styled(Body)`
  font-size: 12px;
  margin: 0;
`;

const ViewLink = styled.a`
  color: ${colors.blue100};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
